import React, { useState } from "react";
import { ShoppingBag, X, ChevronDown, ChevronUp } from "lucide-react";
import { computePrice } from "../lib/pricing";

export default function Cart({ state, onRemove, onGoToStep }) {
  const [open, setOpen] = useState(false);
  const { items, total } = computePrice(state);

  return (
    <div
      data-testid="cart-container"
      className="fixed z-40 bottom-4 left-4"
      style={{ width: open ? "min(340px, calc(100vw - 32px))" : undefined }}
    >
      {/* Compact floating pill — shown on all screens when closed */}
      {!open && (
        <button
          data-testid="cart-pill"
          onClick={() => setOpen(true)}
          className="flex items-center gap-2 pl-3 pr-4 py-2.5 rounded-full border shadow-xl backdrop-blur-md active:scale-95 transition-transform"
          style={{
            backgroundColor: "rgba(255,255,255,0.96)",
            borderColor: "rgba(184,153,104,0.4)",
            boxShadow: "0 8px 28px rgba(44,42,40,0.22)",
          }}
        >
          <span className="relative flex items-center justify-center">
            <ShoppingBag size={20} strokeWidth={1.6} style={{ color: "#8E6E3F" }} />
            <span
              className="absolute -top-2 -right-2 min-w-[16px] h-[16px] px-1 flex items-center justify-center rounded-full text-[10px] font-bold"
              style={{ backgroundColor: "#8E6E3F", color: "#fff" }}
            >
              {items.length}
            </span>
          </span>
          <span className="font-serif-display text-lg leading-none" style={{ color: "#2C2A28" }}>
            ${total}
          </span>
        </button>
      )}

      {/* Full card — shown only when open, on all screens */}
      <div
        className={`${open ? "block" : "hidden"} rounded-2xl border shadow-xl backdrop-blur-md`}
        style={{
          backgroundColor: "rgba(255,255,255,0.96)",
          borderColor: "rgba(184,153,104,0.4)",
          boxShadow: "0 12px 40px rgba(44,42,40,0.18)",
          width: "min(340px, calc(100vw - 32px))",
        }}
      >
        <button
          data-testid="cart-toggle"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-between px-5 py-4 rounded-2xl"
        >
          <div className="flex items-center gap-3">
            <ShoppingBag size={20} strokeWidth={1.6} style={{ color: "#8E6E3F" }} />
            <div className="text-left">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: "#8E6E3F" }}>
                Your Gelato
              </div>
              <div className="font-serif-display text-2xl leading-none mt-0.5" style={{ color: "#2C2A28" }}>
                ${total}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold px-2 py-1 rounded-full" style={{ backgroundColor: "#F5EFE0", color: "#8E6E3F" }}>
              {items.length}
            </span>
            {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </div>
        </button>

        {open && (
          <div data-testid="cart-items" className="px-5 pb-4 max-h-[55vh] overflow-y-auto border-t" style={{ borderColor: "rgba(184,153,104,0.2)" }}>
            {items.map((it, idx) => {
              const isFirst = idx === 0; // Quantity row — not removable
              return (
                <div
                  key={`${it.key || "qty"}-${it.label}-${idx}`}
                  className="flex items-start justify-between py-3 border-b last:border-b-0"
                  style={{ borderColor: "rgba(184,153,104,0.15)" }}
                >
                  <div className="flex-1 min-w-0 pr-2">
                    <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: "#8E6E3F" }}>
                      {it.category}
                    </div>
                    <div className="text-sm font-medium mt-0.5 truncate" style={{ color: "#2C2A28" }}>
                      {it.label}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-sm font-bold" style={{ color: "#2C2A28" }}>
                      ${it.price}
                    </span>
                    {!isFirst && (
                      <button
                        data-testid={`cart-remove-${idx}`}
                        onClick={() => onRemove(it)}
                        className="w-6 h-6 rounded-full flex items-center justify-center transition"
                        style={{ backgroundColor: "rgba(184,153,104,0.15)", color: "#8E6E3F" }}
                        aria-label={`Remove ${it.label}`}
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {items.length === 1 && (
              <div className="text-xs text-center py-3" style={{ color: "#75716C" }}>
                Add ingredients to see them here.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
